(() => {
  const initializedRoots = new WeakSet();

  function initCatalog(root) {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const productsContainer = root.querySelector('[data-catalog-products]');
    const searchInput = root.querySelector('[data-catalog-search]');
    const typeFilter = root.querySelector('[data-catalog-type-filter]');
    const visibleCount = root.querySelector('[data-catalog-visible]');
    const totalCount = root.querySelector('[data-catalog-total]');
    const loadingStatus = root.querySelector('[data-catalog-loading]');
    const noResults = root.querySelector('[data-catalog-no-results]');
    const printButtons = [...root.querySelectorAll('[data-catalog-print]')];
    let allPagesPromise;

    const productCards = () => [...root.querySelectorAll('[data-catalog-product]')];

    function updateGroupHeadings() {
      if (!productsContainer) return;
      let activeHeading = null;
      let activeHeadingHasProducts = false;

      const commitHeading = () => {
        if (activeHeading) activeHeading.hidden = !activeHeadingHasProducts;
      };

      [...productsContainer.children].forEach((element) => {
        if (element.matches('[data-catalog-group]')) {
          commitHeading();
          activeHeading = element;
          activeHeadingHasProducts = false;
        } else if (element.matches('[data-catalog-product]') && !element.hidden) {
          activeHeadingHasProducts = true;
        }
      });

      commitHeading();
    }

    function applyFilters() {
      const query = (searchInput?.value || '').trim().toLowerCase();
      const selectedType = typeFilter?.value || '';
      let shown = 0;

      productCards().forEach((card) => {
        const matchesSearch = !query || (card.dataset.catalogSearchText || '').includes(query);
        const matchesType = !selectedType || card.dataset.productType === selectedType;
        card.hidden = !(matchesSearch && matchesType);
        if (!card.hidden) shown += 1;
      });

      if (visibleCount) visibleCount.textContent = String(shown);
      if (noResults) noResults.hidden = shown !== 0;
      updateGroupHeadings();
    }

    function lastGroupHeading(container) {
      const headings = container?.querySelectorAll('[data-catalog-group]');
      return headings?.length ? headings[headings.length - 1] : null;
    }

    async function loadAllCatalogPages() {
      if (allPagesPromise) return allPagesPromise;

      allPagesPromise = (async () => {
        let nextLink = root.querySelector('[data-catalog-next]');
        let nextUrl = nextLink?.href || '';
        const visited = new Set();

        if (nextUrl && loadingStatus) loadingStatus.hidden = false;

        while (nextUrl && !visited.has(nextUrl)) {
          visited.add(nextUrl);
          const response = await fetch(nextUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`Catalog page failed to load: ${response.status}`);

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const incomingProducts = documentFragment.querySelector('[data-catalog-products]');
          const incomingNext = documentFragment.querySelector('[data-catalog-next]');

          if (!incomingProducts || !productsContainer) break;

          const currentLastHeading = lastGroupHeading(productsContainer);
          const incomingFirstHeading = incomingProducts.querySelector(':scope > [data-catalog-group]');
          if (
            currentLastHeading &&
            incomingFirstHeading &&
            currentLastHeading.textContent.trim() === incomingFirstHeading.textContent.trim()
          ) {
            incomingFirstHeading.remove();
          }

          productsContainer.append(...[...incomingProducts.children]);
          const incomingHref = incomingNext?.getAttribute('href') || '';
          nextUrl = incomingHref ? new URL(incomingHref, window.location.href).href : '';
        }

        const count = productCards().length;
        if (totalCount) totalCount.textContent = String(count);
        if (loadingStatus) loadingStatus.hidden = true;
        applyFilters();
      })().catch((error) => {
        if (loadingStatus) loadingStatus.textContent = 'Some catalog products could not be loaded. Please refresh and try again.';
        throw error;
      });

      return allPagesPromise;
    }

    async function waitForPrintAssets() {
      root.querySelectorAll('.catalog-description details').forEach((details) => {
        details.open = true;
      });

      const images = [...root.querySelectorAll('img')];
      images.forEach((image) => {
        image.loading = 'eager';
      });

      const imagePromises = images.map((image) => {
        if (image.complete) return Promise.resolve();
        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      });

      const fontPromise = document.fonts?.ready || Promise.resolve();
      const timeout = new Promise((resolve) => window.setTimeout(resolve, 8000));
      await Promise.race([Promise.all([fontPromise, ...imagePromises]), timeout]);
    }

    async function printCompleteCatalog() {
      printButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });

      try {
        await loadAllCatalogPages();

        if (searchInput) searchInput.value = '';
        if (typeFilter) typeFilter.value = '';
        applyFilters();
        await waitForPrintAssets();
        window.print();
      } catch (error) {
        console.error(error);
      } finally {
        printButtons.forEach((button) => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        });
      }
    }

    searchInput?.addEventListener('input', applyFilters);
    typeFilter?.addEventListener('change', applyFilters);
    printButtons.forEach((button) => button.addEventListener('click', printCompleteCatalog));

    loadAllCatalogPages().catch(() => {});
    applyFilters();

    const shouldAutoPrint = new URLSearchParams(window.location.search).get('print') === '1';
    const inThemeEditor = root.dataset.designMode === 'true' || window.Shopify?.designMode;
    if (shouldAutoPrint && !inThemeEditor) {
      window.setTimeout(printCompleteCatalog, 150);
    }
  }

  function initAllCatalogs(scope = document) {
    if (scope.matches?.('[data-catalog-root]')) initCatalog(scope);
    scope.querySelectorAll?.('[data-catalog-root]').forEach(initCatalog);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllCatalogs());
  } else {
    initAllCatalogs();
  }

  document.addEventListener('shopify:section:load', (event) => initAllCatalogs(event.target));
})();
